import { type AxiosRequestConfig, type AxiosResponse, type CancelToken } from "axios";
import moment, { type DurationInputArg2, type Moment } from "moment";
import { type ReportColumn } from "../../Common/Reporting/ReportColumns.tsx";
import { type ReportSearchFilter } from "../../Common/Reporting/Search/ReportSearch";
import { type LocalDatabaseCustomer } from "../../Data/_Bases/BaseCustomerRepository";
import type BaseRepository from "../../Data/_Bases/BaseRepository";
import { determineDataSource } from "../../Data/DataStores/DataSource";
import ReportParser, {
    type ReportClauses,
    type WhereClause,
    type WhereClauses,
} from "../Domain/Reports/ReportParser";
import { compare } from "../Generic/Objects";
import { uppercaseFirst } from "../Generic/Strings";
import Request, { CacheRequest } from "./Request";

export interface ReportType {
    key: string;
    completed: boolean;
    data: ReportData;
}

export type RawReportData = Record<string, string | number> | GroupType;

type ReportGroupMap = Record<string, GroupType>;

export type RawReportDataLayer = Array<RawReportData> | ReportGroupMap;

type ReportOrder = keyof RawReportDataLayer | Array<ReportOrder>;

export interface ReportData {
    columns: Array<string>;
    totals: Record<string, null | string>;
    range: {
        start: string;
        end: string;
    };
    groups: Array<string>;
    data: RawReportDataLayer;
    widest: Record<string, string>;
    nullGroupNames: Array<string>;
    order: Array<ReportOrder>;
}

export interface GroupType {
    totals: Record<string, string>;
    name: string;
    data: RawReportDataLayer;
}

interface ReportOptions {
    ignoreDivisionByZero: null | boolean;
    disableFormat: null | boolean;
    sendWidestValues: null | boolean;
    serverCache: null | {
        expiry: Moment;
        id: string;
    };
    cancelToken?: false | CancelToken | AbortSignal;
    source: "monolith" | "microservice";
}

interface ReportErrorType {
    error: string;
    message: string;
}

export function getReport(
    query: string,
    key: null | string = null,
    options: Partial<ReportOptions> = {},
    cache = false,
    page = 1
): Promise<ReportType> {
    options = {
        ignoreDivisionByZero: false,
        disableFormat       : false,
        sendWidestValues    : false,
        cancelToken         : false,
        serverCache         : null,
        source              : "monolith",
        ...options,
    };

    const reportOptions: AxiosRequestConfig = {};

    if(options.cancelToken) {
        if(options.cancelToken instanceof AbortSignal) {
            reportOptions.signal = options.cancelToken;
        } else {
            reportOptions.cancelToken = options.cancelToken;
        }
    }

    const request = cache ? CacheRequest : Request;

    return request.post("/reports", {
        query,
        key,
        disableFormat       : options.disableFormat,
        ignoreDivisionByZero: options.ignoreDivisionByZero,
        sendWidestValues    : options.sendWidestValues,
        cache               : options.serverCache,
        source              : options.source,
        page,
    }, reportOptions)
        .then((response: AxiosResponse<ReportErrorType | ReportType>) => {
            if((response.data as ReportErrorType).error) {
                throw new Error((response.data as ReportErrorType).message);
            }

            return response.data as ReportType;
        });
}

function mergeLayer(layer: RawReportDataLayer, results: RawReportDataLayer): RawReportDataLayer {
    // Check if layer is data or contains children layers
    if(Array.isArray(layer)) {
        // Push new data
        for(let i = 0, l = layer.length; i < l; i++) {
            (results as Array<RawReportData>).push(layer[i]);
        }
    } else {
        // Iterate through children layers and merge
        results = results as Record<string, GroupType>;

        for(const group in layer) {
            if(Object.prototype.hasOwnProperty.call(layer, group)) {
                // Get child
                const child = layer[group];

                // Check if this is the first time that the child layer has been merged
                if(!results[group]) {
                    results[group] = {
                        name  : child.name,
                        totals: child.totals,
                        data  : Array.isArray(child.data) ? [] : {},
                    };
                }

                // Recursively merge
                mergeLayer(child.data, results[group].data);
            }
        }
    }

    return results;
}

export function mergeReportData(reports: Array<ReportType>): RawReportDataLayer {
    if(!reports.length) {
        return [];
    }

    // Prepare the first layer of the data
    const results: RawReportDataLayer = (Array.isArray(reports[0].data.data) ? [] : {});

    // Loop through all the data
    for(let i = 0, l = reports.length; i < l; i++) {
        // Merge first layer of data (Data structure and building is done via reference)
        mergeLayer(reports[i].data.data, results);
    }

    return results;
}

export async function getFullReport(
    query: string,
    options: Partial<ReportOptions> = {},
    cache = false,
    fixKeyOrder = false
): Promise<ReportType> {
    const data: Array<ReportType> = [];
    let completed = false;
    let key = null;

    // Get all the report parts
    let page = 1;

    while(!completed) {
        // I believe the type is required because of this bug:
        // https://github.com/microsoft/TypeScript/issues/35546
        const report: ReportType = await getReport(query, key, options, cache, page++);

        if(report.completed) {
            completed = true;
        }

        if(report.key) {
            key = report.key;
        }

        data.push(report);
    }

    // Format the data
    const report = {
        completed,
        key : key as string,
        data: {
            widest        : data[0].data.widest,
            columns       : data[0].data.columns,
            totals        : data[0].data.totals,
            data          : mergeReportData(data),
            range         : data[0].data.range,
            groups        : data[0].data.groups,
            order         : data[0].data.order,
            nullGroupNames: data[0].data.nullGroupNames,
        },
    };

    if(fixKeyOrder) {
        return orderReport(report);
    }

    return report;
}

function orderLayer(dataLayer: RawReportDataLayer, order: ReportOrder): RawReportDataLayer {
    if(Array.isArray(dataLayer)) {
        return dataLayer;
    }

    if(!Array.isArray(order)) {
        return dataLayer;
    }

    const newDataLayer: RawReportDataLayer = {};

    for(let i = 0, l = order.length; i < l; i++) {
        const currentOrder = order[i];

        if(Array.isArray(currentOrder)) {
            if(typeof currentOrder[0] === "string") {
                // key
                if(typeof dataLayer[currentOrder[0]] === "undefined") {
                    // For another page of data
                    continue;
                }

                newDataLayer[`ordered_${currentOrder[0]}`] = {
                    ...dataLayer[currentOrder[0]],
                    data: orderLayer(
                        dataLayer[currentOrder[0]].data,
                        currentOrder.slice(1)
                    ),
                };
            } else {
                for(let j = 0, n = currentOrder.length; j < n; j++) {
                    const deepOrder = currentOrder[j];

                    if(!Array.isArray(deepOrder)) {
                        throw new TypeError("Invalid deep ordering provided (deep order should be an array)");
                    }

                    if(typeof deepOrder[0] === "string") {
                        // Key
                        if(typeof dataLayer[deepOrder[0]] === "undefined") {
                            continue;
                        } else {
                            newDataLayer[`ordered_${deepOrder[0]}`] = {
                                ...dataLayer[deepOrder[0]],
                                data: orderLayer(
                                    dataLayer[deepOrder[0]].data,
                                    deepOrder.slice(1)
                                ),
                            };
                        }
                    } else {
                        throw new TypeError("Invalid deep ordering provided (deep order should have a key)");
                    }
                }
            }
        } else {
            newDataLayer[`ordered_${currentOrder}`] = (dataLayer as any)[currentOrder];
        }
    }

    return newDataLayer;
}

export function orderReport(data: ReportType): ReportType {
    if(data.data.order.length === 0) {
        return data;
    }

    return {
        ...data,
        data: {
            ...data.data,
            data: orderLayer(data.data.data, data.data.order),
        },
    };
}

const TIME_CONSTANTS = [ "TODAY", "CURRENT_TIMESTAMP", "ALL_OF_TIME" ];

/**
 * Assemble a report from clauses
 * @param {ReportClauses} clauses
 * @param {boolean} format
 * @returns {string}
 */
export function assembleReport(clauses: ReportClauses, format = false): string {
    let query = "";

    // ----- SELECT Clause -----
    query += "SELECT";

    if(format) {
        query += "\n";
    } else {
        query += " ";
    }

    for(let i = 0, l = clauses.select.length; i < l; i++) {
        if(format) {
            query += "\t";
        }

        query += clauses.select[i] + ",";

        if(format) {
            query += "\n";
        } else {
            query += " ";
        }
    }

    query = query.slice(0, -2);

    if(format) {
        query += "\n";
    } else {
        query += " ";
    }

    // ----- FROM Clause -----
    query += "FROM " + clauses.from;

    if(format) {
        query += "\n";
    } else {
        query += " ";
    }

    // ----- PRECISION Clause -----
    if(clauses.precision) {
        if(clauses.precision.type === "range") {
            query += "RANGE ";
        } else {
            query += "AGGREGATE ";
        }

        query += clauses.precision.level;

        if(format) {
            query += "\n";
        } else {
            query += " ";
        }
    }

    // ----- BY Clause -----
    query += "BY " + clauses.by;

    if(format) {
        query += "\n";
    } else {
        query += " ";
    }

    // ----- ON Clause -----
    if(clauses.on.length) {
        query += "ON ";

        for(let i = 0, l = clauses.on.length; i < l; i++) {
            query += clauses.on[i] + ", ";
        }

        query = query.slice(0, -2);

        if(format) {
            query += "\n";
        } else {
            query += " ";
        }
    }

    // ----- WHERE Clause -----
    if(clauses.where.length) {
        query += "WHERE";

        for(let i = 0, l = clauses.where.length; i < l; i++) {
            if(format && i === 0) {
                query += "\n\t";
            } else {
                query += " ";
            }

            const whereClause = clauses.where[i] as WhereClause;

            if(typeof whereClause === "string") {
                query += whereClause;
                continue;
            }

            if(typeof whereClause.field !== "undefined") {
                query += whereClause.field;
            }

            let inOperator = false;

            if(typeof whereClause.operator !== "undefined" && !!whereClause.operator) {
                query += " " + whereClause.operator + " ";

                if(whereClause.operator.toLowerCase().includes("in")) {
                    inOperator = true;
                }
            }

            if(
                typeof whereClause.value === "string" ||
                whereClause.value === null ||
                typeof whereClause.value === "number"
            ) {
                // Check to see if the in clause requires the value to be surrounded
                let value = whereClause.value;

                if(value !== null) {
                    value = value.toString().trim();

                    if(inOperator && (!value.startsWith("(") || !value.endsWith(")"))) {
                        value = `(${value})`;
                    }
                } else {
                    value = "NULL";
                }

                query += value;
            } else {
                if(typeof whereClause.field === "undefined") {
                    query += "(" + whereClause.value.value + ")";
                } else if(typeof whereClause.value.value !== "undefined") {
                    // Check to see if the in clause requires the value to be surrounded
                    let value = whereClause.value.value.trim();

                    if(inOperator && (!value.startsWith("(") || !value.endsWith(")"))) {
                        value = `(${value})`;
                    }

                    query += value;
                }
            }

            if(i + 1 < l) {
                if(format) {
                    query += "\n\t";
                } else {
                    query += " ";
                }

                if(whereClause.and) {
                    query += "AND";
                } else {
                    query += "OR";
                }
            }
        }

        if(format) {
            query += "\n";
        } else {
            query += " ";
        }
    }

    // ----- START Clause -----
    if(clauses.start) {
        let start = clauses.start;

        if(!TIME_CONSTANTS.includes(start)) {
            start = "\"" + start + "\"";
        }

        query += "START " + start;
        // query += ' START "' + clauses.start.format('Y-MM-DD HH:mm:ss') + '"';

        if(format) {
            query += "\n";
        } else {
            query += " ";
        }
    }

    // ----- END Clause -----
    if(clauses.end) {
        let end = clauses.end;

        if(!TIME_CONSTANTS.includes(end)) {
            end = "\"" + end + "\"";
        }

        query += "END " + end;
        // query += ' END "' + clauses.end.format('Y-MM-DD HH:mm:ss') + '"';

        if(format) {
            query += "\n";
        } else {
            query += " ";
        }
    }

    // ----- ORDER Clause -----
    if(clauses.order.length) {
        query += "ORDER";

        for(let i = 0, l = clauses.order.length; i < l; i++) {
            query += " \"" + clauses.order[i].field + "\" " + clauses.order[i].direction + ",";
        }

        query = query.slice(0, -1);

        if(format) {
            query += "\n";
        } else {
            query += " ";
        }
    }

    // ----- CONSOLIDATED Clause -----
    if(clauses.consolidated) {
        query += "CONSOLIDATED";
    } else {
        query += "UNCONSOLIDATED";
    }

    if(format) {
        query += "\n";
    } else {
        query += " ";
    }

    // ----- STATUS Clause -----
    if(clauses.status) {
        query += clauses.status.visibility.toUpperCase() + " " + clauses.status.type?.toUpperCase();

        if(format) {
            query += "\n";
        } else {
            query += " ";
        }
    }

    // ----- LIMIT Clause -----
    if(clauses.limit) {
        query += "LIMIT " + clauses.limit;

        if(format) {
            query += "\n";
        } else {
            query += " ";
        }
    }

    // ----- OFFSET Clause -----
    if(clauses.offset) {
        query += "OFFSET " + clauses.offset;
    }

    return query.trim();
}

/**
 * Parse a report string into clauses
 * @param {string} report
 * @return {ReportClauses}
 */
export function parseReport(report: string): ReportClauses {
    const parser = new ReportParser(report);

    return parser.parse();
}

/**
 * Generate a name for the report based off clauses
 * @param {ReportClauses} clauses
 * @returns {string}
 */
export function generateReportName(clauses: ReportClauses): string {
    let from = "";
    const fromParts = clauses.from.split(/[_.,]/g);

    for(let i = 0, l = fromParts.length; i < l; i++) {
        from += `${uppercaseFirst(fromParts[i].trim())} `;
    }

    from = from.trim();

    let by = "";
    const byParts = clauses.by.split(/[_.,]/g);

    for(let i = 0, l = byParts.length; i < l; i++) {
        by += `${uppercaseFirst(byParts[i].trim())} `;
    }

    by = by.trim();

    let group = "";

    if(clauses.on.length) {
        group = " grouped by ";

        for(let i = 0, l = clauses.on.length; i < l; i++) {
            group += uppercaseFirst(clauses.on[i]) + " and ";
        }

        group = group.slice(0, -5);
    }

    return from + " by " + by + group;
}

export type AvailableColumn = ReportColumn & {
    total?: "SUM" | "AVERAGE" | "MODE" | "MEDIAN" | "PERCENTAGE_FROM_TOTAL" | "NONE";
    conditions: {
        consolidated: Array<string> | boolean;
        type: Array<string>; // An empty array means available to all
        group: Array<string>;
        precision: Array<{ type: string; level: string; }>;
    } | (
        (
            consolidated: boolean,
            type: string,
            group: false | string,
            precision: false | {
                type: "range" | "aggregate";
                level?: string;
            },
            additional?: string
        ) => boolean
    );
    default: Array<string> | ((type: string, consolidated: boolean, groupBy: false | string) => boolean);
};

/**
 * Determine which columns are currently selectable based upon the column conditions
 */
export function determineAvailableColumns(
    columns: Array<AvailableColumn>,
    consolidated: boolean,
    type: string,
    group: false | string,
    precision: false | { type: "range" | "aggregate"; level?: string; },
    additional?: string
): Array<ReportColumn> {
    const availableColumns = [];
    let hasSelectFirstColumn = false;

    for(let i = 0, l = columns.length; i < l; i++) {
        const conditions = columns[i].conditions;

        if(typeof conditions === "function") {
            if(!conditions(consolidated, type, group, precision, additional)) {
                continue;
            }
        } else {
            if(consolidated) {
                let matches = true;
                let ignores = false;

                if(Array.isArray(conditions.consolidated)) {
                    for(let j = 0, n = conditions.consolidated.length; j < n; j++) {
                        if(type === conditions.consolidated[j]) {
                            matches = true;
                            ignores = true;
                            break;
                        }

                        if(conditions.consolidated[j].charAt(0) === "!") {
                            ignores = true;

                            if(conditions.consolidated[j].slice(1) === type) {
                                matches = false;
                                break;
                            }
                        }
                    }
                } else {
                    matches = true;
                    ignores = conditions.consolidated;
                }

                if(!ignores && matches) {
                    matches = false;
                }

                if(!matches) {
                    continue;
                }
            }

            if(conditions.type.length) {
                let matches = true;
                let ignores = false;

                for(let j = 0, n = conditions.type.length; j < n; j++) {
                    if(type === conditions.type[j]) {
                        matches = true;
                        ignores = true;
                        break;
                    }

                    if(conditions.type[j].charAt(0) === "!") {
                        ignores = true;

                        if(conditions.type[j].slice(1) === type) {
                            matches = false;
                            break;
                        }
                    }
                }

                if(!ignores && matches) {
                    matches = false;
                }

                if(!matches) {
                    continue;
                }
            }

            if(conditions.group.length) {
                let matches = true;
                let ignores = false;

                for(let j = 0, n = conditions.group.length; j < n; j++) {
                    if(group === conditions.group[j]) {
                        matches = true;
                        ignores = true;
                        break;
                    }

                    if(conditions.group[j].charAt(0) === "!") {
                        ignores = true;

                        if(conditions.group[j].slice(1) === group) {
                            matches = false;
                            break;
                        }
                    }
                }

                if(!ignores && matches) {
                    matches = false;
                }

                if(!matches) {
                    continue;
                }
            }

            if(conditions.precision.length) {
                let matches = false;

                for(let j = 0, n = conditions.precision.length; j < n; j++) {
                    if(compare(precision, conditions.precision[j])) {
                        matches = true;
                        break;
                    }
                }

                if(!matches) {
                    continue;
                }
            }
        }

        let selected = false;
        const columnDefault = columns[i].default;

        if(typeof columnDefault === "function") {
            selected = columnDefault(type, consolidated, group);
        } else if(columnDefault.includes(type) || columnDefault.includes("*")) {
            selected = true;
        } else if(consolidated && (columnDefault.includes("#" + type) || columnDefault.includes("#*"))) {
            selected = true;
        } else if(!consolidated && (columnDefault.includes("!#" + type) || columnDefault.includes("!#*"))) {
            selected = true;
        }

        if(precision !== false) {
            selected = !hasSelectFirstColumn;
            hasSelectFirstColumn = true;
        }

        availableColumns.push({
            ...columns[i],
            selected,
        });
    }

    return availableColumns;
}

/**
 * Load a filter from the server (used after loadFilterFromLocal)
 * @param filter
 * @returns {Promise<void>}
 */
export function loadFilterFromServer(filter: ReportSearchFilter): Promise<void> {
    const type = filter.field.toLowerCase();

    if(type === "by_name" && filter.value === "NULL") {
        filter.name = "Unclassified Data";
        filter.deleted = false;
        filter.parent = "";
        filter.value = null;

        return Promise.resolve();
    }

    return Request.post("/graphql", {
        query: `query GetReportFilter($id: ID!, $type: String!) {
            getReportFilter(id: $id, type: $type) {
                name,
                parent,
                deleted
            }
        }`,
        variables: {
            type,
            id: filter.value,
        },
    })
        .then(response => {
            const data = response.data.data.getReportFilter;

            filter.name = data.name;
            filter.deleted = data.deleted;
            filter.parent = data.parent || "";
        });
}

/**
 * Load a filter from the local database
 * @param filter
 * @returns {Promise<void>}
 */
export async function loadFilterFromLocal(filter: ReportSearchFilter): Promise<void> {
    const source = await determineDataSource();

    let table = filter.field.toLowerCase() + "s";
    let special = false;

    switch(table) {
        case "categorys":
            table = "categories";
            break;
        case "customers":
            special = true;
            break;
        case "groups":
            table = "customerGroups";
            break;
    }

    if(!table) {
        throw new Error("Could not determine what table to use");
    }

    let data;

    try {
        data = await (source[table as keyof typeof source] as BaseRepository<object>).get(filter.value as string) as {
            name: string;
        };
    } catch {
        data = {
            name: "",
        };
    }

    let name = "";
    const parent = "";

    if(special) {
        switch(table) {
            case "customers":
                name = (data as unknown as LocalDatabaseCustomer).full_name;
                break;
        }
    } else {
        name = data.name;
    }

    filter.name = name;
    filter.deleted = false;
    filter.parent = parent;
}

async function loadFilter(filter: ReportSearchFilter): Promise<void> {
    try {
        await loadFilterFromLocal(filter);
    } catch(e) {
        try {
            await loadFilterFromServer(filter);
        } catch(e) {
            filter.disconnected = true;
        }
    }
}

const VALID_FILTER_OPERATORS = [ "<>", "=", "not in", "in" ];

/**
 * Convert a parsed where clause into report filters
 * @param where
 * @returns {Array}
 */
export function convertWhereToFilters(where: Array<WhereClauses>): Promise<Array<ReportSearchFilter>> {
    const filters: Array<ReportSearchFilter> = [];

    for(let i = 0, l = where.length; i < l; i++) {
        const whereClause = where[i];

        if(Array.isArray(whereClause)) {
            throw new Error("Nested where statements cannot be converted");
        }

        if(typeof whereClause === "string") {
            throw new Error("Raw where statements cannot be converted");
        }

        if(typeof whereClause.field === "undefined" || typeof whereClause.operator === "undefined") {
            throw new Error("Raw where statements cannot be converted");
        }

        if(whereClause.field === "BY_NAME") {
            let value = whereClause.value;

            if(typeof value === "number") {
                value = value.toString();
            }

            if(typeof value !== "string") {
                value = value.value.toUpperCase();
            } else {
                if(value.charAt(0) === "\"") {
                    value = value.slice(1, -1);
                }

                value = value.toUpperCase();
            }

            if(value === "NULL") {
                const operatorUpper = whereClause.operator?.toUpperCase();

                filters.push({
                    type    : "Miscellaneous",
                    included: !operatorUpper.includes("<>") && !operatorUpper.includes("NOT"),
                    value   : "NULL",
                    deleted : false,
                    parent  : undefined,
                    field_id: undefined,
                    field   : "BY_NAME",
                    prepend : "",
                    name    : "Unclassified Data",
                });
                continue;
            }
        }

        if(typeof whereClause.value !== "string") {
            throw new Error("Raw where statements cannot be converted");
        }

        const operator = whereClause.operator.toLowerCase();

        if(!VALID_FILTER_OPERATORS.includes(operator)) {
            throw new Error("Only '=', '<>', 'IN' and 'NOT IN' where statements can be converted");
        }

        let appendId = false;
        let isId = false;
        let field = whereClause.field;
        let prepend = "";

        if(field.endsWith("_id")) {
            field = field.slice(0, -3);
            appendId = true;
        } else if(field.endsWith(".id")) {
            isId = true;
            field = field.slice(0, -3);
            prepend = field;
        }

        const fieldSplit = field.split(".");

        if(fieldSplit.length > 1) {
            field = fieldSplit.pop() as string;
            prepend = fieldSplit.join(".");
        }

        if(field.endsWith("s") && field.toLowerCase() !== "status") {
            field = field.slice(0, -1);
        }

        let values = [];

        if(operator.includes("in")) {
            values = whereClause.value.split(", ");
        } else {
            values.push(whereClause.value);
        }

        for(let i = 0, l = values.length; i < l; i++) {
            let value = values[i];

            if(value.charAt(0) === "\"") {
                value = value.slice(1, -1);
            }

            if(field === "discount" && value.toString() === "0" && whereClause.operator === "<>") {
                continue;
            }

            let name = value;

            if(field === "action") {
                name = value.split("-").map(split => uppercaseFirst(split)).join(" ");
            }

            let type = uppercaseFirst(field);

            if(type === "Method") {
                type = "Payment Method";
            }

            filters.push({
                included: operator === "=" || operator === "in",
                value   : value,
                deleted : false,
                parent  : "",
                field_id: isId ? "id" : `${field}${appendId ? "_id" : ""}`,
                prepend,
                field,
                type,
                name,
            });
        }
    }

    const loading = [];

    for(let i = 0, l = filters.length; i < l; i++) {
        loading.push(loadFilter(filters[i]));
    }

    return Promise.all(loading)
        .then(() => filters);
}

/**
 * Convert a parsed select statement to columns
 * @param select
 * @param columns
 * @param consolidated
 * @param type
 * @param group
 * @param precision
 * @returns {Array}
 */
export function convertSelectToColumns(
    select: Array<string>,
    columns: Array<AvailableColumn>,
    consolidated: boolean,
    type: string,
    group: false | string,
    precision: false | { type: "range" | "aggregate"; level?: string; }
): Array<ReportColumn> {
    const results = [];

    const available = determineAvailableColumns(
        columns,
        consolidated,
        type,
        group,
        precision
    );

    const selectColumns = [];

    for(let i = 0, l = select.length; i < l; i++) {
        selectColumns.push(select[i].split(" AS ")[0]);
    }

    for(let i = 0, l = available.length; i < l; i++) {
        const matching = selectColumns.find(column => column === available[i].value);

        results.push({
            ...available[i],
            selected: !!matching,
        });
    }

    return results;
}

/**
 * Parse a report date into a moment object
 * @param date
 * @param isStart
 * @returns {moment}
 */
export function parseReportDate(date: string, isStart?: boolean): false | Moment {
    date = date.toLowerCase();
    let parsed: Moment | false = moment(date);

    // Check to see if the date object given to the function is a valid date time
    if(parsed.isValid()) {
        // Return correct value
        return parsed;
    } else {
        // Create new moment object
        parsed = moment();
    }

    const parts = date.split(" ");
    const last = parts.pop() as string;

    let timeSet = false;

    if(/^([0-2][0-9]):([0-5][0-9]):([0-5][0-9])$/gi.test(last)) {
        // Set the time values
        const [ hour, minute, second ] = last.split(":");

        parsed.hour(parseInt(hour)).minute(parseInt(minute)).second(parseInt(second));
        timeSet = true;
    } else {
        // Add last back on the end
        parts.push(last);
    }

    if(parts[0] === "today") {
        if(!timeSet) {
            parsed = false;
        }
    } else if(parts[0] === "yesterday") {
        parsed.endOf("day").subtract(1, "day");
    } else {
        const time = parseInt(parts[0]);
        const period = parts[1];

        parsed.subtract(time, period as DurationInputArg2);
    }

    if(parsed && !timeSet) {
        if(isStart === true) {
            parsed.startOf("day");
        } else if(isStart === false) {
            parsed.endOf("day");
        }
    }

    return parsed;
}

export type ReportDynamicPeriodType = "day" | "week" | "month" | "year" | "days" | "weeks" | "months" | "years";

export type ReportDynamicOptionType = {
    dynamic: boolean;
    type: ReportDynamicPeriodType;
    amount: number;
    inclusive: boolean;
};

export function isReportDynamicPeriodType(value: string): value is ReportDynamicPeriodType {
    return [ "day", "week", "month", "year", "days", "weeks", "months", "years" ].includes(value);
}

export function getDynamicRange(report: ReportClauses): ReportDynamicOptionType {
    // Check to see if end date is dynamic
    if(!report.end || (!report.end.includes("today") && !report.end.includes("yesterday"))) {
        return {
            dynamic  : false,
            type     : "day",
            amount   : 1,
            inclusive: true,
        };
    }

    let amount;
    let type;

    // Get the start date dynamic values
    if(typeof report.start === "string") {
        [ amount, type ] = report.start.split(" ");
        amount = parseFloat(amount);
    }

    if(!amount || !type || isNaN(amount) || !isReportDynamicPeriodType(type)) {
        return {
            dynamic  : false,
            type     : "day",
            amount   : 1,
            inclusive: true,
        };
    }

    return {
        dynamic  : true,
        type     : type,
        amount   : amount,
        inclusive: report.end === "today",
    };
}
